/**
 * Pure formatting helpers for the Staffing board header sub-info line
 * ("Day Shift • Jul 8, 2026 • 1st Half • IAH1" in the mock).
 *
 * Kept separate from the page component so each part can be unit-tested in
 * isolation and reused. All functions are pure and return `null` when the
 * underlying value is missing, so the header can filter out unavailable
 * parts and degrade gracefully.
 */

import { ShiftOption } from '../../../utils/constants';

/**
 * Map a stored shift value (e.g. `"day"`) to its human label plus the word
 * "Shift" (e.g. `"Day Shift"`), matching the mock. Falls back to a
 * capitalised form of the raw value when it isn't one of the known options,
 * and returns `null` when no shift is selected.
 */
export function formatShiftLabel(
  shift: string | null | undefined,
  options: ShiftOption[]
): string | null {
  if (!shift) return null;
  const match = options.find(o => o.value === shift);
  const label = match
    ? match.label
    : shift.charAt(0).toUpperCase() + shift.slice(1).toLowerCase();
  return `${label} Shift`;
}

/**
 * Map a stored half value (e.g. `"firstHalfStationAssignment"`) to a compact
 * ordinal label (`"1st Half"` / `"2nd Half"`), matching the mock. Falls back
 * to the option's own label for any unrecognised value, and returns `null`
 * when no half is provided.
 */
export function formatHalfLabel(
  half: string | null | undefined,
  options: ShiftOption[]
): string | null {
  if (!half) return null;
  if (half === 'firstHalfStationAssignment') return '1st Half';
  if (half === 'secondHalfStationAssignment') return '2nd Half';
  const match = options.find(o => o.value === half);
  return match ? match.label : null;
}

/**
 * Format an ISO-ish date string (e.g. `"2026-07-08"`) as the mock's compact
 * `"Jul 8, 2026"`. Parses the `YYYY-MM-DD` prefix directly (rather than via
 * `new Date`) so the rendered day is not shifted by the viewer's timezone.
 * Returns `null` for a missing or unparseable value.
 */
export function formatBoardDate(date: string | null | undefined): string | null {
  if (!date) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}
